import { MutableRefObject, useCallback, useEffect, useRef } from 'react'

import type { F0FormRef } from '@factorialco/f0-react'

import { VendorContactDetailsDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { ContactSelectorV2 } from 'modules/finance/components/Contacts/ContactSelectorV2'

type AccountResource = {
  operationType?: string | null
  account: {
    id: string
    name?: string | null
    number: string
    legalEntity: { id: string }
  }
}

interface VendorFieldProps {
  value: string
  onChange: (value: string) => void
  label: string
  required: boolean
  companyId: string
  /** F0FormRef to set computed field values */
  formRef: MutableRefObject<F0FormRef | null>
  /** Form field ID (definition.id) for the vendor_id_number computed field */
  vendorIdNumberFieldId: string | null
  /** Form field ID (definition.id) for the address computed field */
  addressFieldId: string | null
  /** Form field ID (definition.id) for the vendor_email computed field */
  vendorEmailFieldId: string | null
  /** Form field ID (definition.id) for the vendor_ledger_account computed field */
  vendorLedgerAccountFieldId: string | null
  /** Current legal entity ID from the form, used to resolve the vendor's ledger account */
  legalEntityId: string | null
}

function formatVendorAddress(address: {
  line1: string
  line2?: string | null
  postalCode: string
  city: string
  state: string
  countryCode: string
}): string {
  return [
    address.line1,
    address.line2,
    address.postalCode,
    address.city,
    address.state,
    address.countryCode,
  ]
    .filter(Boolean)
    .join(', ')
}

function resolveVendorLedgerAccount(
  accountResources: ReadonlyArray<AccountResource>,
  legalEntityId: string | null
): string {
  if (!legalEntityId) return ''

  // Prefer purchase-specific ledger account over legacy null operationType
  // to avoid non-deterministic resolution when both exist for the same legal entity.
  const purchaseMatch = accountResources.find(
    (ar) => ar.operationType === 'purchase' && ar.account.legalEntity.id === legalEntityId
  )
  const fallbackMatch = accountResources.find(
    (ar) => ar.operationType === null && ar.account.legalEntity.id === legalEntityId
  )
  const match = purchaseMatch ?? fallbackMatch

  if (!match) return ''

  const parts = [match.account.number, match.account.name].filter(Boolean)
  return parts.join(' - ')
}

function VendorField({
  value,
  onChange,
  label,
  required,
  companyId,
  formRef,
  vendorIdNumberFieldId,
  addressFieldId,
  vendorEmailFieldId,
  vendorLedgerAccountFieldId,
  legalEntityId,
}: VendorFieldProps) {
  // Keep the last fetched account resources so we can recompute the ledger
  // account when the legal entity changes without re-fetching the vendor.
  const accountResourcesRef = useRef<ReadonlyArray<AccountResource>>([])

  // Track the latest legalEntityId so async callbacks (onCompleted) always
  // read the most recent value instead of a stale closure capture.
  const legalEntityIdRef = useRef(legalEntityId)
  useEffect(() => {
    legalEntityIdRef.current = legalEntityId
  }, [legalEntityId])

  const [fetchContact] = useLazyQuery(VendorContactDetailsDocument, {
    onCompleted(data) {
      const contact = data.finance.contactsConnection.edges[0]?.node
      if (!contact) return

      accountResourcesRef.current = contact.accountResources ?? []

      if (vendorIdNumberFieldId) {
        formRef.current?.setValue(vendorIdNumberFieldId, contact.taxId ?? '')
      }
      if (addressFieldId) {
        const formatted = contact.address ? formatVendorAddress(contact.address) : ''
        formRef.current?.setValue(addressFieldId, formatted)
      }
      if (vendorEmailFieldId) {
        formRef.current?.setValue(vendorEmailFieldId, contact.email ?? '')
      }
      if (vendorLedgerAccountFieldId) {
        const ledgerValue = resolveVendorLedgerAccount(
          accountResourcesRef.current,
          legalEntityIdRef.current
        )
        formRef.current?.setValue(vendorLedgerAccountFieldId, ledgerValue)
      }
    },
  })

  // When editing an existing PO, the vendor is already selected but
  // accountResourcesRef is empty because fetchContact only runs on
  // user-initiated changes. Fetch on mount so legal entity changes
  // can recompute the ledger account.
  const didFetchInitialRef = useRef(false)
  useEffect(() => {
    if (didFetchInitialRef.current || !value) return
    didFetchInitialRef.current = true
    fetchContact({ variables: { ids: [value] } })
  }, [value, fetchContact])

  // Recompute ledger account when legalEntityId changes (e.g. user picks a
  // different legal entity after having already selected a vendor).
  const prevLegalEntityIdRef = useRef(legalEntityId)
  useEffect(() => {
    if (prevLegalEntityIdRef.current === legalEntityId) return
    prevLegalEntityIdRef.current = legalEntityId

    if (vendorLedgerAccountFieldId && accountResourcesRef.current.length > 0) {
      const ledgerValue = resolveVendorLedgerAccount(accountResourcesRef.current, legalEntityId)
      formRef.current?.setValue(vendorLedgerAccountFieldId, ledgerValue)
    }
  }, [legalEntityId, vendorLedgerAccountFieldId, formRef])

  const clearComputedFields = useCallback(() => {
    if (vendorIdNumberFieldId) {
      formRef.current?.setValue(vendorIdNumberFieldId, '')
    }
    if (addressFieldId) {
      formRef.current?.setValue(addressFieldId, '')
    }
    if (vendorEmailFieldId) {
      formRef.current?.setValue(vendorEmailFieldId, '')
    }
    if (vendorLedgerAccountFieldId) {
      formRef.current?.setValue(vendorLedgerAccountFieldId, '')
    }
  }, [
    vendorIdNumberFieldId,
    addressFieldId,
    vendorEmailFieldId,
    vendorLedgerAccountFieldId,
    formRef,
  ])

  const handleChange = useCallback(
    (id: string | undefined) => {
      const newValue = id ?? ''
      onChange(newValue)

      if (newValue) {
        fetchContact({ variables: { ids: [newValue] } })
      } else {
        clearComputedFields()
      }
    },
    [onChange, fetchContact, clearComputedFields]
  )

  return (
    <ContactSelectorV2
      label={label}
      value={value || undefined}
      onChange={handleChange}
      forcedFilters={{ companyId, contactType: 'vendor' as const }}
      mandatory={required}
      hideSearchIcon
    />
  )
}

export default VendorField
